<template>
  <div class="persona-board">
    <header class="persona-heading">
      <h1>목적별 분석</h1>
      <p>목적을 선택한 뒤 아래로 내려가며 차트를 순서대로 확인하세요.</p>
    </header>
    <div class="persona-purpose-tabs" role="group" aria-label="나의 분석 목적">
      <button v-for="item in courses" :key="item.id" type="button" :aria-pressed="courseId===item.id" @click="selectCourse(item.id)">{{ item.short }}</button>
    </div>
    <nav class="persona-steps" aria-label="분석 단계 바로가기">
      <button v-for="item in course.steps" :key="item.id" type="button" :aria-current="stepNumber===item.id?'step':null" @click="selectStep(item.id)">
        <span aria-hidden="true">{{ item.id }}</span><strong>{{ guides[courseId][item.id].title }}</strong>
      </button>
    </nav>
    <div class="persona-course-cache">
      <!-- Fixed slots retain iframe ancestors without moving them on a cache hit. -->
      <div v-for="(cached,index) in cachedCourses" :key="index" v-show="cached && cached.id===courseId" class="persona-course-slot">
        <div v-if="cached" :key="cached.id" :data-persona-course="cached.id">
          <section v-for="item in cached.steps" :id="stepId(item.id,cached.id)" :key="cached.id+':'+item.id" ref="stepSections" class="persona-analysis" :data-step="item.id" :data-course="cached.id" :aria-labelledby="stepId(item.id,cached.id)+'-heading'">
            <header class="persona-step-heading">
              <span>{{ cached.short }} · {{ item.id }} / {{ cached.steps.length }}</span>
              <h2 :id="stepId(item.id,cached.id)+'-heading'" tabindex="-1">{{ guides[cached.id][item.id].title }}</h2>
              <p>{{ guides[cached.id][item.id].description }}</p>
            </header>
            <persona-flow-chart v-for="chart in item.charts" :key="chart.url" :chart="chart" :context-key="cached.id+':'+item.id+':'+chart.url" :active="active && cached.id===courseId" />
          </section>
        </div>
      </div>
    </div>
    <details v-show="step.groups.length" class="persona-optional persona-region" :open="regionExpanded" @toggle="toggleRegion">
      <summary><span>지역 분석 함께 보기</span><small>가격·거래·공급을 함께 비교</small></summary>
      <div class="persona-optional-body"><original-analysis-board v-if="regionOpened" :group-ids="regionGroupIds" :active="active && regionExpanded" /></div>
    </details>
    <details class="persona-optional" :open="notesExpanded" @toggle="toggleNotes">
      <summary><span>내 메모·계산 <small>(선택)</small></span><small>저장한 기록·예산 계산·내려받기</small></summary>
      <persona-notes v-if="notesOpened" :course="course" :step="step" :plan="plan" :save-status="saveStatus" @save="save" @download="download" />
    </details>
  </div>
</template>
<script>
import curriculum from './courses.json';
import { ReadingGuide } from './reading-guide';
import { emptyPlan, restorePlan, decisionDocument } from './planning';
import PersonaFlowChart from './PersonaFlowChart.vue';
import PersonaNotes from './PersonaNotes.vue';
import OriginalAnalysisBoard from '../native/OriginalAnalysisBoard.vue';

export default {
  name:'PersonaBoard',
  components:{PersonaFlowChart,PersonaNotes,OriginalAnalysisBoard},
  props:{active:{type:Boolean,default:true}},
  data(){
    const courses=curriculum.courses;
    const query=this.$route?this.$route.query:Object.fromEntries(new URLSearchParams(location.search));
    const selected=courses.some(course=>course.id===query.persona)?query.persona:courses[0].id;
    return {
      courses,guides:ReadingGuide,courseId:selected,stepNumber:/^[1-5]$/.test(String(query.step))?Number(query.step):1,
      cachedCourseIds:[selected,null],courseUseOrder:[selected],
      plans:Object.fromEntries(courses.map(course=>[course.id,emptyPlan()])),saveStatus:'',
      regionOpened:false,regionExpanded:false,regionGroupIds:[],notesOpened:false,notesExpanded:false
    };
  },
  computed:{
    storagePrefix(){const profile=this.$store && this.$store.state.profile;return profile && profile.userId?'expert.persona.v1.'+encodeURIComponent(profile.userId)+'.':this.$router?null:'expert.persona.review.v1.';},
    course(){return this.courses.find(course=>course.id===this.courseId);},
    cachedCourses(){return this.cachedCourseIds.map(id=>this.courses.find(course=>course.id===id)||null);},
    step(){return this.course.steps[this.stepNumber-1];},
    guide(){return this.guides[this.courseId][this.stepNumber];},
    plan(){return this.plans[this.courseId];}
  },
  watch:{
    courseId(value){this.retainCourse(value);},
    storagePrefix(){this.loadRecords();},
    active(value){if(value)this.$nextTick(this.updateVisibleStep);},
    plan:{deep:true,handler(){if(this.saveStatus.startsWith('이 브라우저에 저장했습니다.'))this.saveStatus='저장 후 변경한 내용이 있습니다. 다시 저장해 주세요.';}},
    'plan.steps':{deep:true,handler(steps){steps.forEach(record=>{if(record.done && ['finding','source','risk','next'].some(key=>!record[key].trim()))record.done=false;});}},
    'step.groups'(groups){if(this.regionExpanded)this.regionGroupIds=groups.slice();},
    '$route.query'(query){if(this.$route.name==='insight')this.applyQuery(query);}
  },
  created(){this.loadRecords();},
  mounted(){
    window.addEventListener('scroll',this.onScroll,{passive:true});
    if(this.stepNumber>1)this.$nextTick(()=>this.scrollToStep(this.stepNumber));
  },
  beforeDestroy(){window.removeEventListener('scroll',this.onScroll);if(this._scrollFrame)window.cancelAnimationFrame(this._scrollFrame);},
  methods:{
    retainCourse(id){
      if(!this.courses.some(course=>course.id===id))return;
      if(!this.cachedCourseIds.includes(id)){
        const empty=this.cachedCourseIds.indexOf(null);
        const slot=empty!==-1?empty:this.cachedCourseIds.indexOf(this.courseUseOrder[0]);
        this.$set(this.cachedCourseIds,slot,id);
      }
      this.courseUseOrder=this.courseUseOrder.filter(cached=>cached!==id && this.cachedCourseIds.includes(cached)).concat(id);
    },
    applyQuery(query){
      const course=this.courses.some(course=>course.id===query.persona)?query.persona:this.courses[0].id;
      const step=/^[1-5]$/.test(String(query.step))?Number(query.step):1;
      const changed=course!==this.courseId || step!==this.stepNumber;
      this.courseId=course;this.stepNumber=step;
      if(changed && this.active)this.$nextTick(()=>this.scrollToStep(step));
    },
    stepId(id,courseId=this.courseId){return 'persona-step-'+courseId+'-'+id;},
    scrollToStep(id){
      if(!this.active)return;
      const section=(this.$refs.stepSections||[]).find(element=>element.id===this.stepId(id));
      if(section && typeof section.scrollIntoView==='function')section.scrollIntoView({behavior:'auto',block:'start'});
    },
    onScroll(){
      if(!this.active || this._scrollFrame)return;
      this._scrollFrame=window.requestAnimationFrame(()=>{this._scrollFrame=null;this.updateVisibleStep();});
    },
    updateVisibleStep(){
      if(!this.active)return;
      const sections=(this.$refs.stepSections||[]).filter(section=>section.dataset.course===this.courseId);
      let visible=null;
      for(const section of sections){
        const box=section.getBoundingClientRect();
        // Above the stream use its first stage; gaps retain the last stage passed.
        if(box.height && (visible===null || box.top<=190))visible=Number(section.dataset.step);
      }
      if(visible && visible!==this.stepNumber){this.stepNumber=visible;this.syncUrl();}
    },
    loadRecords(){
      this.plans=Object.fromEntries(this.courses.map(course=>[course.id,emptyPlan()]));this.saveStatus='';
      if(!this.storagePrefix)return;
      this.courses.forEach(course=>{try{const saved=localStorage.getItem(this.storagePrefix+course.id);if(saved)this.$set(this.plans,course.id,restorePlan(JSON.parse(saved)));}catch(_){this.saveStatus='일부 저장 기록을 읽지 못했습니다. 다른 과정의 기록은 계속 사용할 수 있습니다.';}});
    },
    selectCourse(id){
      if(id===this.courseId || !this.courses.some(course=>course.id===id))return;
      this.courseId=id;this.stepNumber=1;this.saveStatus='';this.syncUrl();
      this.$nextTick(()=>this.scrollToStep(1));
    },
    selectStep(step){if(!/^[1-5]$/.test(String(step)))return;this.stepNumber=Number(step);this.syncUrl();this.$nextTick(()=>this.scrollToStep(this.stepNumber));},
    syncUrl(){
      if(this.$router){
        const query={...this.$route.query,tab:'persona',persona:this.courseId,step:String(this.stepNumber)};
        if(this.$route.query.persona!==this.courseId || String(this.$route.query.step)!==String(this.stepNumber))this.$router.replace({path:'/insight',query});
      }else{
        const url=new URL(location.href);url.searchParams.set('persona',this.courseId);url.searchParams.set('step',String(this.stepNumber));url.hash='persona';
        history.replaceState(null,'',url.pathname+url.search+url.hash);
      }
    },
    toggleRegion(event){
      this.regionExpanded=event.target.open;
      if(this.regionExpanded){this.regionGroupIds=this.step.groups.slice();this.regionOpened=true;}
    },
    toggleNotes(event){this.notesExpanded=event.target.open;if(this.notesExpanded)this.notesOpened=true;},
    save(){
      if(!this.storagePrefix){this.saveStatus='로그인 상태를 확인한 뒤 저장해 주세요.';return;}
      try{localStorage.setItem(this.storagePrefix+this.courseId,JSON.stringify(this.plan));this.saveStatus='이 브라우저에 저장했습니다. '+new Date().toLocaleTimeString('ko-KR');}
      catch(_){this.saveStatus='브라우저에 저장하지 못했습니다. 분석 기록을 내려받아 보관해 주세요.';}
    },
    download(){
      const blob=new Blob([decisionDocument(this.course,this.plan)],{type:'text/markdown;charset=utf-8'});
      const url=URL.createObjectURL(blob),anchor=document.createElement('a');anchor.href=url;anchor.download=`리치고_${this.course.short}_분석기록.md`;anchor.click();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }
  }
};
</script>
<style>@import './persona.css';</style>
